import { T } from '@tolgee/react';
import { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { ORANGE_TINT } from './primitives';

// Region resolution — map region/country from organization data into the
// state-content bucket we show in the "Did you know?" block.
// TX → Texas / STAAR · CA → California / CAASPP · OTHER → generic US · INTL → hidden.
export type StatRegion = 'TX' | 'CA' | 'OTHER' | 'INTL';

export function resolveStatRegion(
  region: string | undefined,
  country: string | undefined,
): StatRegion {
  if (country !== 'US') return 'INTL';
  if (region === 'TX') return 'TX';
  if (region === 'CA') return 'CA';
  return 'OTHER';
}

export function isInternational(country: string | undefined): boolean {
  return country !== 'US';
}

// ─── Content ──────────────────────────────────────────────────────────────

interface StatContent {
  percent: string;
  percentLabel: ReactNode;
  headline: ReactNode;
  body: ReactNode;
  links: Array<{ label: ReactNode; href: string }>;
}

const TX_CONTENT: StatContent = {
  percent: '30%',
  percentLabel: <T keyName="BlockedPaywall.Stat.Label.Higher" defaultValue="higher" />,
  headline: (
    <T
      keyName="BlockedPaywall.Stat.TX.Headline"
      defaultValue="Growth in Meets and Masters on STAAR"
    />
  ),
  body: (
    <T
      keyName="BlockedPaywall.Stat.TX.Body"
      defaultValue="Teachers using Curipod 3+ times per week see double-digit growth in students' STAAR scores."
    />
  ),
  links: [
    {
      label: (
        <T keyName="BlockedPaywall.Stat.Link.Efficacy" defaultValue="Efficacy & Impact" />
      ),
      href: '/case-studies/curipod-case-study-green-valley',
    },
    {
      label: (
        <T
          keyName="BlockedPaywall.Stat.Link.RealTime"
          defaultValue="Real-Time Feedback, Real Results"
        />
      ),
      href: '/case-studies/curipod-case-study-south-east-texas',
    },
  ],
};

const CA_CONTENT: StatContent = {
  percent: '40%',
  percentLabel: <T keyName="BlockedPaywall.Stat.Label.Higher" defaultValue="higher" />,
  headline: (
    <T
      keyName="BlockedPaywall.Stat.CA.Headline"
      defaultValue="Higher writing scores on CAASPP"
    />
  ),
  body: (
    <T
      keyName="BlockedPaywall.Stat.CA.Body"
      defaultValue="Teachers using Curipod 3+ times per week see double-digit growth in students' CAASPP scores."
    />
  ),
  links: [
    {
      label: (
        <T keyName="BlockedPaywall.Stat.Link.Efficacy" defaultValue="Efficacy & Impact" />
      ),
      href: '/case-studies/curipod-case-study-southern-ca',
    },
    {
      label: (
        <T
          keyName="BlockedPaywall.Stat.Link.CA.Targeted"
          defaultValue="Targeted Writing, Immediate Feedback"
        />
      ),
      href: '/case-studies/california-writing-achievement',
    },
  ],
};

const OTHER_CONTENT: StatContent = {
  percent: '30%',
  percentLabel: <T keyName="BlockedPaywall.Stat.Label.Higher" defaultValue="higher" />,
  headline: (
    <T
      keyName="BlockedPaywall.Stat.Other.Headline"
      defaultValue="Growth in student writing scores"
    />
  ),
  body: (
    <T
      keyName="BlockedPaywall.Stat.Other.Body"
      defaultValue="Teachers using Curipod 3+ times per week see double-digit growth in students' writing scores on state assessments."
    />
  ),
  links: [
    {
      label: (
        <T keyName="BlockedPaywall.Stat.Link.Efficacy" defaultValue="Efficacy & Impact" />
      ),
      href: '/case-studies',
    },
    {
      label: (
        <T
          keyName="BlockedPaywall.Stat.Link.RealTime"
          defaultValue="Real-Time Feedback, Real Results"
        />
      ),
      href: '/case-studies/curipod-case-study-south-east-texas',
    },
  ],
};

const CONTENT_BY_REGION: Record<Exclude<StatRegion, 'INTL'>, StatContent> = {
  TX: TX_CONTENT,
  CA: CA_CONTENT,
  OTHER: OTHER_CONTENT,
};

// ─── StatBlock component ──────────────────────────────────────────────────

const StatWrap = styled.div`
  background: ${theme.colors.paleBeige};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  box-shadow: 0 2px 20px 0 #deaf8d33;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const PercentSide = styled.div`
  text-align: center;
  flex: none;
  padding-right: 6px;

  @media (max-width: 480px) {
    padding-right: 0;
  }
`;

const PercentValue = styled.div`
  font-size: 44px;
  font-weight: 900;
  color: ${theme.colors.orange};
  line-height: 1;
  letter-spacing: -0.02em;
  font-family: 'Nunito', sans-serif;
`;

const PercentLabel = styled.div`
  font-size: 12px;
  color: rgba(40, 47, 51, 0.5);
  margin-top: 4px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CopySide = styled.div`
  flex: 1;
  min-width: 0;
`;

const CopyHeadline = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: ${theme.colors.black};
  margin-bottom: 8px;
  line-height: 1.3;
  font-family: 'Nunito', sans-serif;
`;

const CopyBody = styled.div`
  background: ${ORANGE_TINT};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13.5px;
  color: ${theme.colors.black};
  line-height: 1.5;
  margin-bottom: 10px;
  font-weight: 700;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const StatLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${theme.colors.orangeDark};
  font-size: 12.5px;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
    color: ${theme.colors.orangeDark};
  }
`;

const Chevron = () => (
  <svg
    viewBox="0 0 24 24"
    width={11}
    height={11}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

interface StatBlockProps {
  statRegion: StatRegion;
}

export const StatBlock: FunctionComponent<StatBlockProps> = ({ statRegion }) => {
  if (statRegion === 'INTL') return null;
  const content = CONTENT_BY_REGION[statRegion];

  return (
    <StatWrap>
      <PercentSide>
        <PercentValue>{content.percent}</PercentValue>
        <PercentLabel>{content.percentLabel}</PercentLabel>
      </PercentSide>
      <CopySide>
        <CopyHeadline>{content.headline}</CopyHeadline>
        <CopyBody>{content.body}</CopyBody>
        <LinkRow>
          {content.links.map((link, i) => (
            <StatLink key={i} href={link.href} target="_blank" rel="noopener noreferrer">
              <Chevron />
              {link.label}
            </StatLink>
          ))}
        </LinkRow>
      </CopySide>
    </StatWrap>
  );
};
